import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { advancedPaddingState, styleState } from '../recoil/state/state'
import {
  Flex,
  FlexItem,
  TITLE_VARIANT_M,
  Title,
  SpacingVertical,
  SPACING_16,
  Card,
  Text
} from 'mailup-web-components'
import PerfectScrollbar from 'react-perfect-scrollbar'
import iconDesktop from '../icons/cd-icon-desktop-preview.svg'
import iconMobile from '../icons/cd-icon-mobile-preview.svg'

export const MOBILE = 'MOBILE'
export const DESKTOP = 'DESKTOP'

export const PREVIEW_PARAGRAPH = 'PREVIEW_PARAGRAPH'
export const PREVIEW_BUTTON = 'PREVIEW_BUTTON'
export const PREVIEW_SOCIAL = 'PREVIEW_SOCIAL'

const StyledIconEmptyText = styled(Text)`
  text-align: center;
`

const StyledPerfectScrollBar = styled(PerfectScrollbar)`
  &.ps {
    padding-right: 0 !important;
  }
`
const StyledCard = styled(Card)`
  background-color: ${({ bckColor }) => bckColor} !important;
  padding: 0 !important;
`

const StyledDiv = styled.div`
  max-height: ${({ cardHeight }) => cardHeight}px;
  padding-top: ${({ styles }) =>
    styles ? styles.paddingTop + ' !important' : null};
  padding-right: ${({ styles }) =>
    styles ? styles.paddingRight + ' !important' : null};
  padding-bottom: ${({ styles }) =>
    styles ? styles.paddingBottom + ' !important' : null};
  padding-left: ${({ styles }) =>
    styles ? styles.paddingLeft + ' !important' : null};
  text-align: ${({ styles }) =>
    styles && styles.align ? styles.align + ' !important' : null};
`

const StyledIcon = styled.img`
  width: 24px;
  height: 24px;
  padding-right: 8px;
`

export const Preview = ({ type, preview }) => {
  const intl = useIntl()

  const {
    paragraph: {
      styles: paragraphStyles,
      mobileStyles,
      blockOptions: paragraphBlockOptions
    },
    button: {
      styles: buttonStyles,
      mobileStyles: mobileButtonStyles,
      blockOptions: buttonBlockOptions
    },
    social: {
      icons,
      mobileStyles: mobileSocialStyles,
      blockOptions: socialBlockOptions
    }
  } = useRecoilValue(styleState)

  const {
    buttonDesktopPaddingAdvanced,
    buttonMobilePaddingAdvanced,
    paragraphDesktopPaddingAdvanced,
    paragraphMobilePaddingAdvanced,
    socialMobilePaddingAdvanced
  } = useRecoilValue(advancedPaddingState)

  const getCardHeight = () => {
    if (preview === PREVIEW_PARAGRAPH) {
      return type === DESKTOP ? 420 : 240
    } else if (preview === PREVIEW_BUTTON) {
      return type === DESKTOP ? 722 : 240
    } else if (preview === PREVIEW_SOCIAL) {
      return type === DESKTOP ? 626 : 240
    }
  }

  const StyledParagraph = styled.div`
    width: 100%;
    max-height: ${getCardHeight() - 10}px;
    word-wrap: break-word;
    color: ${paragraphStyles.color};
    font-size: ${paragraphStyles.fontSize};
    font-family: ${paragraphStyles.fontFamily};
    font-weight: ${paragraphStyles.fontWeight};
    line-height: ${paragraphStyles.lineHeight};
    text-align: ${paragraphStyles.textAlign};
    direction: ${paragraphStyles.direction};
    letter-spacing: ${paragraphStyles.letterSpacing};
    paragraph-spacing: ${paragraphStyles.paragraphSpacing};
    a:link {
      color: ${paragraphStyles.linkColor};
      text-decoration: underline;
    }
    a:visited {
      color: ${paragraphStyles.linkColor};
    }
    a:hover {
      color: ${paragraphStyles.linkColor};
    }
    a:active {
      color: ${paragraphStyles.linkColor};
    }
  `

  const StyledParagraphMobile = styled(StyledParagraph)`
    text-align: ${mobileStyles.textAlign} !important;
    font-size: ${mobileStyles.fontSize} !important;
  `

  const StyledButton = styled.button`
    //width: 151px;
    word-break: break-all;
    // max-height: ${getCardHeight() - 30}px;
    color: ${buttonStyles.color};
    font-size: ${buttonStyles.fontSize};
    font-family: ${buttonStyles.fontFamily};
    font-weight: ${buttonStyles.fontWeight};
    line-height: ${buttonStyles.lineHeight};
    background-color: ${buttonStyles.backgroundColor};
    border-bottom: ${buttonStyles.borderBottom};
    border-left: ${buttonStyles.borderLeft};
    border-radius: ${buttonStyles.borderRadius};
    border-right: ${buttonStyles.borderRight};
    border-top: ${buttonStyles.borderTop};

    padding-left: ${buttonStyles.paddingLeft};
    padding-bottom: ${buttonStyles.paddingBottom};
    padding-right: ${buttonStyles.paddingRight};
    padding-top: ${buttonStyles.paddingTop};

    button {
      word-wrap: break-word;
    }
  `

  const StyledButtonMobile = styled(StyledButton)`
    font-size: ${mobileButtonStyles.fontSize} !important;

    padding-left: ${buttonMobilePaddingAdvanced
      ? mobileButtonStyles.paddingLeft
      : buttonStyles.paddingLeft};
    padding-bottom: ${buttonMobilePaddingAdvanced
      ? mobileButtonStyles.paddingBottom
      : buttonStyles.paddingBottom};
    padding-right: ${buttonMobilePaddingAdvanced
      ? mobileButtonStyles.paddingRight
      : buttonStyles.paddingRight};
    padding-top: ${buttonMobilePaddingAdvanced
      ? mobileButtonStyles.paddingTop
      : buttonStyles.paddingTop};
  `

  const StyledSocialImage = styled.img`
    height: 32px;
    padding-right: 10px;
    :last-child {
      padding-right: 0px;
    }
  `

  const getPaddingStyled = () => {
    let obj = {}
    if (preview === PREVIEW_BUTTON) {
      if (type === MOBILE) {
        // sul bee JSON tra block e mobile cambia il nome della prop
        obj = Object.assign({}, buttonBlockOptions)
        obj.align = mobileButtonStyles.textAlign
      } else if (type === DESKTOP) {
        obj = Object.assign({}, buttonBlockOptions)
      }
    }

    if (preview === PREVIEW_SOCIAL) {
      if (type === MOBILE) {
        // sul bee JSON tra block e mobile cambia il nome della prop
        obj = Object.assign({}, mobileSocialStyles)
        obj.align = mobileSocialStyles.textAlign
        if (!socialMobilePaddingAdvanced) {
          obj = { ...socialBlockOptions, align: mobileSocialStyles.textAlign }
        }
      } else if (type === DESKTOP) {
        obj = Object.assign({}, socialBlockOptions)
      }
    }

    if (preview === PREVIEW_PARAGRAPH) {
      return type === DESKTOP
        ? paragraphBlockOptions
        : !paragraphMobilePaddingAdvanced
        ? paragraphBlockOptions
        : mobileStyles
    } else if (preview === PREVIEW_BUTTON) {
      return obj
    } else if (preview === PREVIEW_SOCIAL) {
      return obj
    }
  }

  const getBckColor = () => {
    if (preview === PREVIEW_SOCIAL) {
      if (
        icons.length &&
        (icons[0].type === 't-circle-white' ||
          icons[0].type === 't-only-logo-white' ||
          icons[0].type === 't-outline-circle-white')
      ) {
        return '#D5DADF'
      }
    }
    return '#ffffff'
  }

  return (
    <Flex flexDirection='column'>
      <Flex alignItems='center'>
        <Flex>
          <StyledIcon src={type === DESKTOP ? iconDesktop : iconMobile} />
        </Flex>
        <FlexItem>
          <Title variant={TITLE_VARIANT_M}>
            {intl.formatMessage({
              id:
                type === DESKTOP
                  ? 'Load.ContentDefaults.DesktopPreview'
                  : 'Load.ContentDefaults.MobilePreview'
            })}
          </Title>
        </FlexItem>
      </Flex>
      <SpacingVertical variant={SPACING_16} />
      <FlexItem>
        <StyledCard bckColor={getBckColor()}>
          <StyledPerfectScrollBar>
            {preview === PREVIEW_PARAGRAPH && (
              <StyledDiv
                styles={getPaddingStyled()}
                cardHeight={getCardHeight()}
              >
                {type === DESKTOP && (
                  <StyledParagraph
                    dangerouslySetInnerHTML={{
                      __html: `Nam ullamcorper, justo id elementum ultricies,
              <a href='javascript:void(0)'>nisi mauris porta nisi</a>, quis
              volutpat mauris ante eget ipsum.<div style="height: ${paragraphStyles.paragraphSpacing};"></div>Donec tincidunt purus velit, sit
              amet congue leo viverra eu.
            `
                    }}
                  />
                )}

                {type === MOBILE && (
                  <StyledParagraphMobile
                    styles={getPaddingStyled()}
                    cardHeight={getCardHeight()}
                    dangerouslySetInnerHTML={{
                      __html: `Nam ullamcorper, justo id elementum ultricies,
              <a href='javascript:void(0)'>nisi mauris porta nisi</a>, quis
              volutpat mauris ante eget ipsum.<div style="height: ${paragraphStyles.paragraphSpacing};"></div>Donec tincidunt purus velit, sit
              amet congue leo viverra eu.
            `
                    }}
                  />
                )}
              </StyledDiv>
            )}
            {preview === PREVIEW_BUTTON && type === DESKTOP && (
              <StyledDiv
                styles={getPaddingStyled()}
                cardHeight={getCardHeight()}
              >
                <StyledButton>PREVIEW TEXT</StyledButton>
              </StyledDiv>
            )}
            {preview === PREVIEW_BUTTON && type === MOBILE && (
              <StyledDiv
                styles={getPaddingStyled()}
                cardHeight={getCardHeight()}
              >
                <StyledButtonMobile>PREVIEW TEXT</StyledButtonMobile>
              </StyledDiv>
            )}

            {preview === PREVIEW_SOCIAL && (
              <StyledDiv
                styles={getPaddingStyled()}
                cardHeight={getCardHeight()}
              >
                {icons.length === 0 && (
                  <StyledIconEmptyText variant='light'>
                    {intl.formatMessage({
                      id: 'Load.ContentDefaults.EmptyStatePreviewSocial'
                    })}
                  </StyledIconEmptyText>
                )}

                {icons.length > 0 &&
                  icons.map((e, i) => (
                    <StyledSocialImage
                      alt={e.image.title}
                      title={e.image.title}
                      key={i}
                      src={e.image.src}
                    />
                  ))}
              </StyledDiv>
            )}
          </StyledPerfectScrollBar>
        </StyledCard>
      </FlexItem>
    </Flex>
  )
}

Preview.propTypes = {
  type: PropTypes.oneOf([DESKTOP, MOBILE]).isRequired,
  preview: PropTypes.oneOf([PREVIEW_PARAGRAPH, PREVIEW_BUTTON, PREVIEW_SOCIAL])
    .isRequired
}
